import type {
  AcceptInviteResponse,
  CreateInviteRequest,
  CreateInviteResponse,
  CreateOrgRequest,
  InvitePreview,
  ListOrgsResponse,
  OrgInvite,
  OrgMember,
  OrgRole,
  OrgSummary,
  Organization,
  PlatformUserRow,
  SlugAvailabilityResponse,
  UpdateMemberRequest,
  UpdateOrgRequest,
} from '@leanopx/shared';
import { authHeaders } from './auth-headers';
import { logger } from './logger';

const API_BASE = '/api';

/**
 * Typed error thrown by `getJson` / `sendJson` when the server returns a
 * non-2xx response. Carries the HTTP status + the parsed error envelope
 * so call sites can `instanceof ApiError` and switch on `error.reason`
 * without substring-matching `error.message`.
 *
 * The server's error envelope shape is `{ error: <code-string>, detail?: <any> }`
 * (per `apps/functions/src/http/router.ts` convention). When the body
 * isn't JSON-parseable, `reason` is `null` and the raw body text lives
 * in `bodyText` for diagnostic purposes.
 *
 * `Error.message` is kept in the `"${status} ${reason or body}"` shape so
 * existing logger.warn / debug paths that just dump `.message` keep
 * working. `ApiError extends Error`, so `instanceof Error` still passes.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly reason: string | null;
  readonly detail: unknown;
  readonly bodyText: string;
  constructor(opts: { status: number; reason: string | null; detail: unknown; bodyText: string }) {
    super(`${opts.status} ${opts.reason ?? opts.bodyText.slice(0, 200)}`);
    this.name = 'ApiError';
    this.status = opts.status;
    this.reason = opts.reason;
    this.detail = opts.detail;
    this.bodyText = opts.bodyText;
  }
}

/**
 * Parse a server error body into the `{ reason, detail }` shape that
 * `ApiError` carries. Tolerant of non-JSON bodies (returns `{ reason:
 * null, detail: null }` when JSON.parse fails or the envelope doesn't
 * carry a string `error` field).
 */
function parseErrorBody(bodyText: string): { reason: string | null; detail: unknown } {
  try {
    const json = JSON.parse(bodyText) as { error?: unknown; detail?: unknown };
    const reason = typeof json.error === 'string' ? json.error : null;
    return { reason, detail: json.detail ?? null };
  } catch {
    return { reason: null, detail: null };
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    logger.warn('orgs-client.get failed', {
      path,
      status: res.status,
      body: body.slice(0, 200),
    });
    const { reason, detail } = parseErrorBody(body);
    throw new ApiError({ status: res.status, reason, detail, bodyText: body });
  }
  return (await res.json()) as T;
}

async function sendJson<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    logger.warn('orgs-client.send failed', {
      method,
      path,
      status: res.status,
      body: text.slice(0, 200),
    });
    const { reason, detail } = parseErrorBody(text);
    throw new ApiError({ status: res.status, reason, detail, bodyText: text });
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * `GET /api/orgs`. For a regular member, returns the orgs they belong to.
 * For a superAdmin, returns every org on the platform. `primaryOrgId` is
 * the user's preferred active org (may be null if never set).
 */
export async function listMemberships(): Promise<ListOrgsResponse> {
  return getJson<ListOrgsResponse>('/orgs');
}

export async function getOrg(orgId: string): Promise<Organization> {
  return getJson<Organization>(`/orgs/${encodeURIComponent(orgId)}`);
}

export async function updateOrg(orgId: string, patch: UpdateOrgRequest): Promise<Organization> {
  return sendJson<Organization>('PATCH', `/orgs/${encodeURIComponent(orgId)}`, patch);
}

export async function createOrg(req: CreateOrgRequest): Promise<Organization> {
  return sendJson<Organization>('POST', '/orgs', req);
}

/**
 * `GET /api/orgs/slug-availability?slug=<s>` — live availability probe used
 * by the self-serve `/onboarding/create-org` form. Any-auth endpoint
 * (deliberately not org-scoped — the user hasn't picked an org yet).
 *
 * Returns `{ available: true }` when the slug is free, or
 * `{ available: false, reason: 'taken' | 'invalid' | 'reserved' }`
 * otherwise. Server is the source of truth for `reserved` (it owns
 * `SLUG_RESERVED_WORDS`); the client also runs a regex pre-check to
 * skip the network round-trip on obviously-invalid input.
 */
export async function checkSlugAvailability(slug: string): Promise<SlugAvailabilityResponse> {
  return getJson<SlugAvailabilityResponse>(
    `/orgs/slug-availability?slug=${encodeURIComponent(slug)}`,
  );
}

/**
 * SuperAdmin-only: list every org. Uses the same `/orgs` endpoint as
 * {@link listMemberships}; the server includes every org in the response
 * when the caller is a superAdmin.
 */
export async function listAllOrgs(): Promise<OrgSummary[]> {
  const resp = await getJson<ListOrgsResponse>('/orgs');
  return resp.organizations;
}

export async function listOrgMembers(orgId: string): Promise<OrgMember[]> {
  const { members } = await getJson<{ members: OrgMember[] }>(
    `/orgs/${encodeURIComponent(orgId)}/members`,
  );
  return members;
}

export async function updateOrgMember(
  orgId: string,
  uid: string,
  patch: UpdateMemberRequest,
): Promise<OrgMember> {
  return sendJson<OrgMember>(
    'PATCH',
    `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`,
    patch,
  );
}

export async function removeOrgMember(orgId: string, uid: string): Promise<void> {
  await sendJson<void>(
    'DELETE',
    `/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(uid)}`,
  );
}

export async function listOrgInvites(orgId: string): Promise<OrgInvite[]> {
  const { invites } = await getJson<{ invites: OrgInvite[] }>(
    `/orgs/${encodeURIComponent(orgId)}/invites`,
  );
  return invites;
}

export async function createOrgInvite(
  orgId: string,
  req: CreateInviteRequest,
): Promise<CreateInviteResponse> {
  return sendJson<CreateInviteResponse>('POST', `/orgs/${encodeURIComponent(orgId)}/invites`, req);
}

export async function revokeOrgInvite(orgId: string, token: string): Promise<void> {
  await sendJson<void>(
    'DELETE',
    `/orgs/${encodeURIComponent(orgId)}/invites/${encodeURIComponent(token)}`,
  );
}

/** Unauthenticated-friendly preview — no `X-Org-Id` needed. */
export async function getInvitePreview(token: string): Promise<InvitePreview> {
  const res = await fetch(`${API_BASE}/invites/${encodeURIComponent(token)}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const { reason, detail } = parseErrorBody(text);
    throw new ApiError({ status: res.status, reason, detail, bodyText: text });
  }
  return (await res.json()) as InvitePreview;
}

export async function acceptInvite(token: string): Promise<AcceptInviteResponse> {
  return sendJson<AcceptInviteResponse>('POST', `/invites/${encodeURIComponent(token)}/accept`);
}

// --- Platform superadmin endpoints ----------------------------------------

export async function listPlatformUsers(): Promise<PlatformUserRow[]> {
  const { users } = await getJson<{ users: PlatformUserRow[] }>('/platform/users');
  return users;
}

export async function grantSuperAdmin(uid: string): Promise<void> {
  await sendJson<void>('POST', `/platform/users/${encodeURIComponent(uid)}/grant-superadmin`);
}

export async function revokeSuperAdmin(uid: string): Promise<void> {
  await sendJson<void>('POST', `/platform/users/${encodeURIComponent(uid)}/revoke-superadmin`);
}

export type { OrgRole };
